import './CreatorSetsTab.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdContentCopy, MdEdit } from 'react-icons/md';
import { RiDeleteBinLine } from 'react-icons/ri';
import { useSearchParams } from 'react-router-dom';
import { YouTubeCreatorSetWithMembers } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import { Intent, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { RenameModal } from '@Components/RenameModal/RenameModal';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { TableLoading } from '@Components/TableEmptyState';
import { formatLastEdited } from '@Pages/StreamingMediaPlanner/streaming-media-planner-utils';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { PAGE_TITLE } from '../youtube-planning-constants';
import { BuildRecommendationPrefill, GoogleAdsAccount } from '../youtube-planning-types';
import { CreatorSetDetail } from './CreatorSetDetail';
import { CREATOR_SET_ENTITY, SET_PARAM } from './creator-sets-constants';
import { toDuplicateInput, toEpochMs, toSaveInput } from './creator-sets-utils';
import { useCreatorSets, useDeleteCreatorSet, useSaveCreatorSet } from './hooks/use-creator-sets';

const ROW_HEIGHT = 44;

const NameAction = {
  RENAME: 'rename',
  DUPLICATE: 'duplicate'
} as const;
type NameActionValue = (typeof NameAction)[keyof typeof NameAction];

interface CreatorSetsTabProps {
  adsAccount?: GoogleAdsAccount;
  onUseInBuild: (prefill: BuildRecommendationPrefill) => void;
}

export const CreatorSetsTab = ({ adsAccount, onUseInBuild }: CreatorSetsTabProps) => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: sets, isLoading, error } = useCreatorSets(cid);

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load creator sets",
        message: error.message,
        reportError: error
      });
    }
  }, [error, setError]);

  const openSetId = Number(searchParams.get(SET_PARAM));
  const openSet = sets?.find(set => set.id === openSetId);

  const openSetById = useCallback(
    (id?: number) => setSearchParams(id === undefined ? {} : { [SET_PARAM]: String(id) }),
    [setSearchParams]
  );

  if (openSet) {
    return (
      <div className='creator-sets-tab'>
        {/* Keyed so a different set never inherits the previous one's unsaved drafts. */}
        <CreatorSetDetail
          key={openSet.id}
          set={openSet}
          adsAccount={adsAccount}
          onBack={() => openSetById()}
          onUseInBuild={onUseInBuild}
        />
      </div>
    );
  }

  return (
    <div className='creator-sets-tab'>
      <CreatorSetsList
        sets={sets ?? []}
        isLoading={isLoading}
        error={error}
        setMissing={searchParams.has(SET_PARAM) && sets !== undefined}
        onOpen={set => openSetById(set.id)}
      />
    </div>
  );
};

interface CreatorSetsListProps {
  sets: YouTubeCreatorSetWithMembers[];
  isLoading: boolean;
  error: Error | null;
  // The URL names a set that isn't in the list (deleted, or another company's).
  setMissing: boolean;
  onOpen: (set: YouTubeCreatorSetWithMembers) => void;
}

const CreatorSetsList = ({ sets, isLoading, error, setMissing, onOpen }: CreatorSetsListProps) => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const { mutateAsync: saveSet } = useSaveCreatorSet(cid);
  const { mutateAsync: deleteSet } = useDeleteCreatorSet(cid);
  const [nameAction, setNameAction] = useState<{
    action: NameActionValue;
    set: YouTubeCreatorSetWithMembers;
  }>();

  const applyName = async (name: string) => {
    if (!nameAction) return;
    const { action, set } = nameAction;
    setNameAction(undefined);
    try {
      if (action === NameAction.RENAME) {
        await saveSet(toSaveInput(set, { name }));
        Mixpanel.track(MxE.RecordSaved, { page: PAGE_TITLE, entity: CREATOR_SET_ENTITY });
      } else {
        await saveSet(toDuplicateInput(set, name));
        Mixpanel.track(MxE.RecordCreated, { page: PAGE_TITLE, entity: CREATOR_SET_ENTITY });
      }
    } catch (e) {
      // A duplicate name is a 400 whose message names the conflict, so it's shown as-is.
      setError({ message: e.message, reportError: e });
    }
  };

  const confirmDelete = useCallback(
    (set: YouTubeCreatorSetWithMembers) =>
      setAreYouSure({
        intent: Intent.ERROR,
        title: 'Delete creator set?',
        message: `"${set.name}" will be deleted for everyone at this company.`,
        okayText: 'Delete',
        onOkay: async () => {
          try {
            await deleteSet(set.id);
            Mixpanel.track(MxE.RecordDeleted, { page: PAGE_TITLE, entity: CREATOR_SET_ENTITY });
          } catch (e) {
            setError({ message: e.message, reportError: e });
          }
        }
      }),
    [deleteSet, setAreYouSure, setError]
  );

  const headers = useMemo<TypedHeader<YouTubeCreatorSetWithMembers>[]>(
    () => [
      {
        name: 'name',
        label: 'Name',
        flex: 1,
        renderer: set => (
          <InteractiveDiv
            className='creator-sets-cell creator-sets-name'
            onClick={() => onOpen(set)}
          >
            {set.name}
          </InteractiveDiv>
        )
      },
      {
        name: 'creators',
        label: 'Creators',
        width: 120,
        contentGetter: set => set.members.length,
        renderer: set => (
          <InteractiveDiv className='creator-sets-cell' onClick={() => onOpen(set)}>
            {set.members.length}
          </InteractiveDiv>
        )
      },
      {
        name: 'lastuser',
        label: 'Last Editor',
        width: 240,
        renderer: set => (
          <InteractiveDiv className='creator-sets-cell' onClick={() => onOpen(set)}>
            {set.lastuser || '—'}
          </InteractiveDiv>
        )
      },
      {
        name: 'lastmodified',
        label: 'Last Edited',
        width: 180,
        contentGetter: set => toEpochMs(set.lastmodified),
        renderer: set => (
          <InteractiveDiv className='creator-sets-cell' onClick={() => onOpen(set)}>
            {formatLastEdited(toEpochMs(set.lastmodified))}
          </InteractiveDiv>
        )
      },
      {
        name: 'actions',
        label: '',
        width: 136,
        nonInteractive: true,
        nonFilterable: true,
        renderer: set => (
          <div className='creator-sets-actions' data-testid={`creator-set-actions-${set.id}`}>
            <IconButton
              size={Size.SM}
              tooltip={{ text: 'Rename' }}
              aria-label='Rename'
              onClick={() => setNameAction({ action: NameAction.RENAME, set })}
            >
              <MdEdit />
            </IconButton>
            <IconButton
              size={Size.SM}
              tooltip={{ text: 'Duplicate' }}
              aria-label='Duplicate'
              onClick={() => setNameAction({ action: NameAction.DUPLICATE, set })}
            >
              <MdContentCopy />
            </IconButton>
            <IconButton
              size={Size.SM}
              tooltip={{ text: 'Delete' }}
              aria-label='Delete'
              onClick={() => confirmDelete(set)}
            >
              <RiDeleteBinLine />
            </IconButton>
          </div>
        )
      }
    ],
    [onOpen, confirmDelete]
  );

  const renderNoRows = useCallback(
    () => (
      <TableLoading
        isLoading={isLoading}
        error={error}
        errorMessage="Couldn't load creator sets"
        emptyMessage='No creator sets yet. Save creators from the Discover Creators tab to start one.'
        rowHeight={ROW_HEIGHT}
      />
    ),
    [isLoading, error]
  );

  return (
    <div className='creator-sets-card' data-testid='creator-sets-list'>
      <div className='planning-form-header'>
        <div className='header'>Creator Sets</div>
        <div className='sub-header'>
          {isLoading
            ? 'Saved shortlists of YouTube creators for this company.'
            : `${conditionallyPluralizeWithCount(sets.length, 'saved set')} for this company.`}
        </div>
      </div>
      {setMissing && (
        <div className='creator-sets-missing' data-testid='creator-set-missing'>
          That creator set doesn&apos;t exist anymore. It may have been deleted.
        </div>
      )}
      <div className='creator-sets-table'>
        <BPMTable
          data={sets}
          headers={headers}
          filterBar={false}
          rowHeight={ROW_HEIGHT}
          noRowsRenderer={renderNoRows}
        />
      </div>
      {nameAction && (
        <RenameModal
          title={
            nameAction.action === NameAction.RENAME ? 'Rename creator set' : 'Duplicate creator set'
          }
          subTitle={
            nameAction.action === NameAction.RENAME
              ? `Rename "${nameAction.set.name}" to:`
              : `Save a copy of "${nameAction.set.name}" as:`
          }
          placeholder='Creator set name'
          applyText={nameAction.action === NameAction.RENAME ? 'Rename' : 'Duplicate'}
          cancelText='Cancel'
          disabled={name => !name.trim()}
          applyOnClick={applyName}
          onCancel={() => setNameAction(undefined)}
          onHide={() => setNameAction(undefined)}
        />
      )}
    </div>
  );
};
